<script setup lang="ts">
import {computed, ref, watchEffect} from 'vue'
import {Decimal} from 'decimal.js'
import {useContext, useDialogs, useRegistry} from '@hantera/portal-app'
import {DecimalInput, DropdownButton, DropdownItem, TextInput} from '@hantera/design-system'
import {findIsoCurrency, ISO_CURRENCIES} from '@/constants/iso-currencies'

const context = useContext()
const dialogs = useDialogs()
const registry = useRegistry()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = 'Currencies'
  }
})

const canRead = computed(() => context.authorize('registry/currencies:read'))
const canWrite = computed(() => context.authorize('registry/currencies:write'))

interface Currency {
  code: string
  label: string
  decimals: Decimal
  exchangeRate: Decimal
}

const currencies = ref<Currency[]>([])
const originals = ref<Record<string, Currency>>({})
const loading = ref(true)
const saving = ref(false)

const CURRENCY_KEY_PATTERN = /^[A-Za-z0-9_]{1,16}$/

function snapshot(c: Currency): Currency {
  return {
    code: c.code,
    label: c.label,
    decimals: new Decimal(c.decimals),
    exchangeRate: new Decimal(c.exchangeRate),
  }
}

function isRowDirty(c: Currency): boolean {
  const original = originals.value[c.code]
  if (!original) return false
  return c.label !== original.label
    || !c.decimals.eq(original.decimals)
    || !c.exchangeRate.eq(original.exchangeRate)
}

const dirtyCount = computed(() => currencies.value.filter(isRowDirty).length)
const isDirty = computed(() => dirtyCount.value > 0)

async function loadCurrencies() {
  loading.value = true
  try {
    const data = await registry.list('currencies/', false)

    const loaded = Object.keys(data).map(fullKey => {
      const code = fullKey.split('/').pop() || ''
      const entry = data[fullKey] || {}
      return {
        code,
        label: entry.label ?? '',
        decimals: new Decimal(entry.decimals ?? 2),
        exchangeRate: new Decimal(entry.exchangeRate ?? 1),
      } as Currency
    }).sort((a, b) => a.code.localeCompare(b.code))

    currencies.value = loaded
    originals.value = Object.fromEntries(loaded.map(c => [c.code, snapshot(c)]))
  } catch (e) {
    console.error('Failed to load currencies:', e)
  } finally {
    loading.value = false
  }
}

loadCurrencies()

async function saveAll() {
  if (!canWrite.value || !isDirty.value || saving.value) return

  saving.value = true
  const failed: string[] = []
  try {
    const dirty = currencies.value.filter(isRowDirty)
    for (const currency of dirty) {
      try {
        await registry.set(`currencies/${currency.code}`, {
          label: currency.label,
          decimals: currency.decimals.toNumber(),
          exchangeRate: currency.exchangeRate.toNumber(),
        })
        originals.value[currency.code] = snapshot(currency)
      } catch (e) {
        console.error(`Failed to save currency ${currency.code}:`, e)
        failed.push(currency.code)
      }
    }
  } finally {
    saving.value = false
  }

  if (failed.length > 0) {
    await dialogs.alert(
      `Failed to save: ${failed.join(', ')}. Other changes were saved successfully.`,
      'Save Error'
    )
  }
}

function resetAll() {
  currencies.value = currencies.value.map(c => {
    const original = originals.value[c.code]
    return original ? snapshot(original) : c
  })
}

function pickIsoCurrency(iso: typeof ISO_CURRENCIES[0]) {
  addCurrency(iso.code, iso.label, iso.decimals)
}

async function addCustomCurrency() {
  const code = prompt('Enter currency code (e.g., "BTC", "LOYALTY_POINTS"):')
  if (!code || code.trim() === '') return

  const normalizedCode = code.trim().toUpperCase()

  if (!CURRENCY_KEY_PATTERN.test(normalizedCode)) {
    await dialogs.alert(
      'Invalid currency code. May only include A-Z, 0-9, and underscores (max 16 chars).',
      'Invalid Code'
    )
    return
  }

  if (currencies.value.some(c => c.code === normalizedCode)) {
    await dialogs.alert('A currency with this code already exists.', 'Duplicate Code')
    return
  }

  const iso = findIsoCurrency(normalizedCode)
  addCurrency(normalizedCode, iso?.label ?? '', iso?.decimals ?? 2)
}

async function addCurrency(code: string, label: string, decimals: number) {
  if (currencies.value.some(c => c.code === code)) {
    await dialogs.alert('A currency with this code already exists.', 'Duplicate Code')
    return
  }

  try {
    await registry.set(`currencies/${code}`, {
      label,
      decimals,
      exchangeRate: 1,
    })
    await loadCurrencies()
  } catch (e) {
    console.error('Failed to create currency:', e)
    await dialogs.alert('Failed to create currency. Please try again.', 'Error')
  }
}

async function deleteCurrency(currency: Currency) {
  if (isRowDirty(currency)) {
    const proceed = await dialogs.confirm(
      `"${currency.code}" has unsaved changes. Delete anyway?`,
      'Unsaved Changes',
      'Delete',
      'Cancel',
      true
    )
    if (!proceed) return
  } else {
    const confirmed = await dialogs.confirm(
      `Are you sure you want to delete currency "${currency.code}"?`,
      'Delete Currency',
      'Delete',
      'Cancel',
      true
    )
    if (!confirmed) return
  }

  try {
    await registry.clear(`currencies/${currency.code}`)
    await loadCurrencies()
  } catch (e) { }
}

const isoFilter = ref('')
const filteredIsoOptions = computed(() => {
  const present = new Set(currencies.value.map(c => c.code))
  const filter = isoFilter.value.toLowerCase()
  return ISO_CURRENCIES
    .filter(iso => !present.has(iso.code))
    .filter(iso =>
      iso.code.toLowerCase().includes(filter) ||
      iso.label.toLowerCase().includes(filter)
    )
})
</script>

<template>
  <div class="currencies-view" v-if="canRead">
    <div class="header">
      <h1>Currencies</h1>
      <div class="header-actions" v-if="canWrite">
        <DropdownButton label="Add ISO Currency" button-style="primary">
          <div class="toolbar">
            <TextInput v-model="isoFilter" placeholder="Filter currencies..." />
          </div>
          <div class="dropdown-scroll">
            <DropdownItem
              v-for="iso in filteredIsoOptions"
              :key="iso.code"
              @click="pickIsoCurrency(iso); isoFilter = ''"
            >
              <span class="iso-code">{{ iso.code }}</span>
              <span class="iso-label">{{ iso.label }}</span>
            </DropdownItem>
            <DropdownItem v-if="filteredIsoOptions.length === 0">
              <em>No matching currencies</em>
            </DropdownItem>
          </div>
        </DropdownButton>
        <button @click="addCustomCurrency" class="btn">
          Add Custom
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-else class="currencies-table-container">
      <table class="currencies-table" v-if="currencies.length > 0">
        <thead>
          <tr>
            <th>Code</th>
            <th>Label</th>
            <th>Decimals</th>
            <th>Exchange Rate</th>
            <th v-if="canWrite" class="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="currency in currencies"
            :key="currency.code"
            :class="{ 'row-dirty': isRowDirty(currency) }"
          >
            <td class="code-column">
              <code>{{ currency.code }}</code>
            </td>
            <td>
              <input
                v-model="currency.label"
                :disabled="!canWrite"
                placeholder="Display name"
                class="input"
              />
            </td>
            <td class="numeric-column">
              <DecimalInput
                v-model="currency.decimals"
                :disabled="!canWrite"
                :min-value="0"
                :max-value="8"
              />
            </td>
            <td class="numeric-column">
              <DecimalInput
                v-model="currency.exchangeRate"
                :disabled="!canWrite"
              />
            </td>
            <td v-if="canWrite" class="actions-column">
              <button
                @click="deleteCurrency(currency)"
                class="btn-danger"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="empty-state">
        <p>No currencies defined.</p>
        <p class="empty-hint">
          Add an ISO 4217 currency from the list, or create a custom code for things like
          loyalty points or cryptocurrencies.
        </p>
      </div>
    </div>

    <div class="save-bar" v-if="canWrite && currencies.length > 0">
      <span class="dirty-indicator" v-if="isDirty">
        {{ dirtyCount }} unsaved {{ dirtyCount === 1 ? 'change' : 'changes' }}
      </span>
      <button :disabled="!isDirty || saving" @click="resetAll">
        Reset
      </button>
      <button class="btn-primary" :disabled="!isDirty || saving" @click="saveAll">
        {{ saving ? 'Saving...' : 'Save' }}
      </button>
    </div>
  </div>

  <div v-else class="no-permission">
    <p>You don't have permission to view currencies.</p>
  </div>
</template>

<style scoped>
.currencies-view {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.loading {
  padding: 40px;
  text-align: center;
  color: var(--color-text-secondary);
}

.currencies-table-container {
  border: 1px solid var(--color-border);
  border-radius: 4px;
  overflow: hidden;
  background: var(--color-background);
}

.currencies-table {
  width: 100%;
  border-collapse: collapse;
}

.currencies-table thead {
  background: var(--color-background-secondary);
}

.currencies-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  border-bottom: 1px solid var(--color-border);
}

.currencies-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
}

.currencies-table tbody tr:last-child td {
  border-bottom: none;
}

.currencies-table tbody tr:hover {
  background: var(--color-background-tertiary);
}

.currencies-table tbody tr.row-dirty {
  background: var(--color-warning-background, hsla(45, 100%, 50%, 0.08));
}

.code-column {
  width: 120px;
}

.code-column code {
  font-family: monospace;
  padding: 2px 6px;
  background: var(--color-background-secondary);
  border-radius: 3px;
  font-size: 0.9em;
}

.numeric-column {
  width: 160px;
}

.actions-column {
  width: 100px;
  text-align: right;
}

.dropdown-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.iso-code {
  font-family: monospace;
  font-weight: 600;
  margin-right: 12px;
  display: inline-block;
  min-width: 50px;
}

.iso-label {
  color: hsl(var(--color-40));
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-state p {
  margin: 0 0 12px 0;
  color: var(--color-text-secondary);
  font-size: 16px;
}

.empty-hint {
  font-size: 14px;
  max-width: 480px;
  margin: 0 auto;
}

.save-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-background-secondary);
}

.dirty-indicator {
  margin-right: auto;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.no-permission {
  padding: 60px 20px;
  text-align: center;
}

.no-permission p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
}
</style>
